import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container, Box, Typography, Paper, Stack, Chip,
  Accordion, AccordionSummary, AccordionDetails,
  Button, IconButton, CircularProgress, Alert,
  Dialog, DialogContent, Snackbar,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Star as TargetIcon,
  CloudDownload as DownloadIcon,
  CloudUpload as CloudUploadIcon,
  Calculate as SolveIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { formatInTimeZone } from 'date-fns-tz';
import FileBreadcrumb from '../../components/files/FileBreadcrumb';
import DownloadProgressDialog from '../../components/files/DownloadProgressDialog';
import FileGrid from '../../components/files/FileGrid';
import ConfirmDialog from '../../components/ConfirmDialog';
import PushExternallyDialog from '../../components/PushExternallyDialog';
import { useFileDownload } from '../../hooks/useFileDownload';
import { useFileWebSocket } from '../../hooks/useFileWebSocket';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessToken } from '../../services/authStorage';

const API_BASE_URL = '/api/v1';

const nestedAccordionSx = {
  '&:before': { display: 'none' },
  boxShadow: 'none',
  borderTop: '1px solid',
  borderColor: 'divider',
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  try {
    return formatInTimeZone(new Date(date), 'UTC', 'yyyy-MM-dd HH:mm') + ' UTC';
  } catch { return 'N/A'; }
};

const ProjectFiles = () => {
  const { projectId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'server_admin' || user?.role === 'admin' || user?.role === 'instructor';
  const { downloadGroup, downloadProgress, cancelDownload, getDownloadUrl, downloadCalibrated } = useFileDownload();
  const [calibrateError, setCalibrateError] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [files, setFiles] = useState([]);
  const [masterCoverage, setMasterCoverage] = useState({});
  const [projectName, setProjectName] = useState('');
  const [previewDialog, setPreviewDialog] = useState({ open: false, url: null, name: null });
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [expandedTargets, setExpandedTargets] = useState(new Set());
  const [expandedObservations, setExpandedObservations] = useState(new Set());
  const [confirm, setConfirm] = useState(null);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getAccessToken();
      const response = await axios.get(`${API_BASE_URL}/user-files/group-files`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { groupType: 'project', groupId: projectId }
      });

      if (response.data.success) {
        const allFiles = response.data.data.files || [];
        setFiles(allFiles);
        setMasterCoverage(response.data.data.master_coverage || {});
        if (allFiles.length > 0) {
          setProjectName(allFiles[0].project_name || 'Unknown Project');
        }
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load project files');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  // Also try to get project name from the by-project grouping (which has enriched names)
  useEffect(() => {
    const fetchProjectName = async () => {
      try {
        const token = getAccessToken();
        const response = await axios.get(`${API_BASE_URL}/user-files/by-project`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          const project = response.data.data.groups?.find(g => g._id?.toString() === projectId);
          if (project) {
            setProjectName(project.project_name);
          }
        }
      } catch { /* ignore - we have a fallback */ }
    };
    fetchProjectName();
  }, [projectId]);

  const handleFileUpdate = useCallback((update) => {
    setFiles(prevFiles =>
      prevFiles.map(file => {
        const fileId = file.file_id || file._id;
        if (fileId === update.file_id) {
          return { ...file, plate_solve_status: update.plate_solve_status, plate_solve_result: update.plate_solve_result || file.plate_solve_result };
        }
        return file;
      })
    );
  }, []);

  useFileWebSocket(handleFileUpdate);

  const getCalibratedDownloadUrl = useCallback((fileId) => {
    const token = getAccessToken();
    return `/api/v1/calibration/download/${fileId}?format=calibrated&token=${encodeURIComponent(token)}`;
  }, []);

  const handleSolveFile = async (fileId) => {
    try {
      const token = getAccessToken();
      await axios.post(`${API_BASE_URL}/user-files/${fileId}/plate-solve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { alert('Failed to queue plate solving job'); }
  };

  const handleDeleteFile = (fileId, filename) => {
    setConfirm({
      title: 'Delete File',
      message: <span>Are you sure you want to delete <strong>{filename}</strong>?</span>,
      confirmLabel: 'Delete',
      confirmColor: 'error',
      confirmIcon: <DeleteIcon />,
      icon: <DeleteIcon color="error" />,
      onConfirm: async () => {
        try {
          const token = getAccessToken();
          await axios.delete(`${API_BASE_URL}/user-files/${fileId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setFiles(prev => prev.filter(f => (f.file_id || f._id) !== fileId));
        } catch (err) {
          setCalibrateError('Failed to delete file');
          throw err;
        }
      }
    });
  };

  const canDeleteFile = useCallback((file) => {
    return isAdmin || file.user_id === user?._id;
  }, [isAdmin, user]);

  const handleSolveAllUnsolved = async () => {
    const unsolved = files.filter(f => f.plate_solve_result?.solved !== true);
    if (unsolved.length === 0) {
      alert('All files are already solved!');
      return;
    }
    setConfirm({
      title: 'Queue Plate Solving',
      message: <span>Queue plate solving for <strong>{unsolved.length}</strong> unsolved file(s)?</span>,
      confirmLabel: 'Queue',
      confirmColor: 'primary',
      confirmIcon: <SolveIcon />,
      icon: <SolveIcon color="primary" />,
      onConfirm: async () => {
        const token = getAccessToken();
        let success = 0;
        for (const file of unsolved) {
          try {
            await axios.post(`${API_BASE_URL}/user-files/${file.file_id || file._id}/plate-solve`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            success++;
          } catch { /* continue */ }
        }
        alert(`Queued ${success} file(s) for plate solving.`);
      }
    });
  };

  const handlePreview = (url, name) => {
    setPreviewDialog({ open: true, url, name });
  };

  const toggleSet = (setter, key) => {
    setter(prev => {
      const s = new Set(prev);
      s.has(key) ? s.delete(key) : s.add(key);
      return s;
    });
  };

  // Group files by target, then by observation within each target
  const targetGroups = files.reduce((acc, file) => {
    const target = file.target_name || 'Unknown Target';
    if (!acc[target]) acc[target] = [];
    acc[target].push(file);
    return acc;
  }, {});

  const sortedTargets = Object.entries(targetGroups).sort(([a], [b]) => a.localeCompare(b));

  // Auto-expand all targets on first load when few targets
  useEffect(() => {
    if (sortedTargets.length > 0 && expandedTargets.size === 0 && sortedTargets.length <= 10) {
      setExpandedTargets(new Set(sortedTargets.map(([name]) => name)));
    }
  }, [files.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <FileBreadcrumb items={[
          { label: 'Files', path: '/files' },
          { label: projectName || 'Project' }
        ]} />

        <Typography variant="h5" gutterBottom>
          {projectName || 'Project Files'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {sortedTargets.length} target{sortedTargets.length !== 1 ? 's' : ''} &middot; {files.length} file{files.length !== 1 ? 's' : ''}
        </Typography>

        {/* Bulk Actions */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={() => downloadGroup('project', projectId)}
            disabled={files.length === 0}
          >
            Download All ({files.length})
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<SolveIcon />}
            onClick={handleSolveAllUnsolved}
            disabled={files.length === 0}
          >
            Solve Unsolved
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => setPushDialogOpen(true)}
            disabled={files.length === 0}
          >
            Push Externally
          </Button>
        </Box>

        {/* Target Accordions */}
        <Stack spacing={2}>
          {sortedTargets.map(([targetName, targetFiles]) => {
            const targetSolved = targetFiles.filter(f => f.plate_solve_result?.solved === true).length;

            // Group by observation within target
            const obsGroups = {};
            targetFiles.forEach(f => {
              const obsId = f.observation_id?.toString() || 'unknown';
              if (!obsGroups[obsId]) obsGroups[obsId] = { name: f.observation_name || obsId, files: [] };
              obsGroups[obsId].files.push(f);
            });
            const sortedObs = Object.entries(obsGroups).sort(([, a], [, b]) => {
              const dateA = a.files[0]?.uploaded_at || '';
              const dateB = b.files[0]?.uploaded_at || '';
              return dateB.localeCompare(dateA);
            });

            return (
              <Accordion
                key={targetName}
                expanded={expandedTargets.has(targetName)}
                onChange={() => toggleSet(setExpandedTargets, targetName)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                    <TargetIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6">{targetName}</Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Chip label={`${targetFiles.length} files`} size="small" />
                      {targetSolved > 0 && (
                        <Chip label={`${targetSolved} solved`} size="small" color="success" variant="outlined" />
                      )}
                    </Stack>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {sortedObs.map(([obsId, obsData]) => {
                    const obsKey = `${targetName}-${obsId}`;
                    const obsSolved = obsData.files.filter(f => f.plate_solve_result?.solved === true).length;

                    return (
                      <Accordion
                        key={obsKey}
                        expanded={expandedObservations.has(obsKey)}
                        onChange={() => toggleSet(setExpandedObservations, obsKey)}
                        disableGutters
                        sx={nestedAccordionSx}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ pl: 4 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle1">{obsData.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(obsData.files[0]?.uploaded_at)}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                              <Chip label={`${obsData.files.length} files`} size="small" />
                              {obsSolved > 0 && (
                                <Chip label={`${obsSolved} solved`} size="small" color="success" variant="outlined" />
                              )}
                            </Stack>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0, pl: 4 }}>
                          <FileGrid
                            files={obsData.files}
                            onPreview={handlePreview}
                            onSolve={handleSolveFile}
                            getDownloadUrl={getDownloadUrl}
                            getCalibratedDownloadUrl={getCalibratedDownloadUrl}
                            downloadCalibrated={downloadCalibrated}
                            onCalibrateError={setCalibrateError}
                            masterCoverage={masterCoverage}
                            onDelete={handleDeleteFile}
                            canDelete={canDeleteFile}
                          />
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>

        {files.length === 0 && !loading && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">No files found for this project</Typography>
          </Paper>
        )}
      </Box>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, url: null, name: null })}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>{previewDialog.name || 'Preview'}</Typography>
          <IconButton onClick={() => setPreviewDialog({ open: false, url: null, name: null })} size="small"><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', bgcolor: '#0a0a0a', minHeight: '400px', maxHeight: '85vh' }}>
          {previewDialog.url && (
            <img src={previewDialog.url} alt={previewDialog.name} style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }}
              onError={(e) => { e.target.style.display = 'none'; }} />
          )}
        </DialogContent>
      </Dialog>

      <DownloadProgressDialog progress={downloadProgress} onClose={cancelDownload} />

      <Snackbar
        open={Boolean(calibrateError)}
        autoHideDuration={8000}
        onClose={() => setCalibrateError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCalibrateError(null)} severity="warning" variant="filled" sx={{ width: '100%' }}>
          {calibrateError}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
        confirmIcon={confirm?.confirmIcon}
        icon={confirm?.icon}
        onConfirm={confirm?.onConfirm}
        onClose={() => setConfirm(null)}
      />

      <PushExternallyDialog
        open={pushDialogOpen}
        onClose={() => setPushDialogOpen(false)}
        projectId={projectId}
        projectName={projectName}
      />
    </Container>
  );
};

export default ProjectFiles;
